import { z } from 'zod'
import type { EtatBoite, NiveauRole } from './domaine'

// Espaces privés : une entité marquée privée n'est visible que de ses titulaires (personnes qui
// y ont un rôle), administrateurs compris. Le circuit (tri des mails, lecture des pièces,
// doublons, fournisseurs, réponses attendues) ne rapproche jamais deux objets de cloisons
// différentes.

/** Cloison de tout ce qui n'est pas dans un espace privé (entités communes, sans entité). */
export const CLOISON_COMMUNE = 'commune'

/** Identifiant d'une entité privée, ou la cloison commune. */
export type Cloison = string

/**
 * Cloison d'un objet d'après son entité : l'entité elle-même si elle est privée, sinon la
 * cloison commune (une entité non privée, ou aucune entité).
 */
export function cloisonDe(
  entiteId: string | null | undefined,
  privees: ReadonlySet<string> | readonly string[],
): Cloison {
  if (!entiteId) return CLOISON_COMMUNE
  const privee = 'has' in privees ? privees.has(entiteId) : privees.includes(entiteId)
  return privee ? entiteId : CLOISON_COMMUNE
}

/** Deux objets ne se rapprochent (doublon, même mail, même fournisseur) que dans la même cloison. */
export function memeCloison(
  a: string | null | undefined,
  b: string | null | undefined,
  privees: ReadonlySet<string> | readonly string[],
): boolean {
  return cloisonDe(a, privees) === cloisonDe(b, privees)
}

// Gouvernance : les rôles d'un espace privé sont gérés par ses titulaires seuls.

/**
 * Titulaire à la création : un espace privé se crée avec sa titulaire (rôle d'associée
 * responsable posé dans la même transaction), et une titulaire ne se désigne que pour lui.
 * Le refus, ou null si la saisie convient.
 */
export function refusTitulaire(e: {
  privee?: boolean
  titulaireId?: string | null
}): string | null {
  if (e.privee && !e.titulaireId) return 'Un espace privé se crée avec sa titulaire.'
  if (!e.privee && e.titulaireId) return 'Une titulaire ne se désigne que pour un espace privé.'
  return null
}

/** Le caractère privé se fixe à la création : ni l'activer ni le retirer ensuite. */
export const PRIVEE_IMMUABLE =
  'Le caractère privé d’une entité se fixe à sa création et ne change plus.'

/**
 * Une règle d'échéance ou un guide de départ « par type » (`typesEntite` des fichiers du seed)
 * ne vise qu'une entité privée de ce type : les entités communes ont leurs règles nommées.
 */
export function viseeParType(
  typesEntite: readonly string[] | undefined,
  e: { type: string; privee: boolean },
): boolean {
  return e.privee && (typesEntite?.includes(e.type) ?? false)
}

/**
 * Niveau qu'un rôle peut avoir sur une entité. Sur un espace privé, associé seulement : le
 * niveau administrateur donnerait à sa titulaire toutes les entités communes.
 */
export function niveauPermisSurEntite(niveau: NiveauRole, privee: boolean): boolean {
  return !privee || niveau === 'associe'
}

/**
 * Un espace privé suppose des titulaires non administratrices : un administrateur voit déjà
 * tout le commun, et le tri de ses boîtes, son menu, sa supervision et l'imputation de son
 * assistant ne seraient plus circonscrits. Ni une personne qui a le niveau administrateur
 * n'entre dans un espace privé (une titulaire ne fait pas trier les boîtes d'un administrateur
 * par les consignes de son espace), ni ce niveau ne s'accorde à qui a un rôle sur un espace
 * privé. Le refus, ou null.
 */
export function refusAdministrateurTitulaire(r: {
  /** Nom de la personne du rôle. */
  nom: string
  /** L'entité du rôle est un espace privé. */
  entitePrivee: boolean
  /** Niveau demandé pour le rôle. */
  niveau: NiveauRole
  /** La personne a le niveau administrateur sur une autre entité. */
  administrateurAilleurs: boolean
  /** La personne a un rôle sur un espace privé. */
  titulaireDUnEspace: boolean
}): string | null {
  if (r.entitePrivee && r.administrateurAilleurs)
    return `${r.nom} a le niveau administrateur : un espace privé n’accueille que des personnes qui ne l’ont pas. Lui retirer d’abord ce niveau, ou choisir une autre personne.`
  if (r.niveau === 'administrateur' && r.titulaireDUnEspace)
    return `${r.nom} a un rôle sur un espace privé : le niveau administrateur ne lui est pas accordé, son espace ne serait plus cloisonné.`
  return null
}

/**
 * Le Drive partagé propre d'un espace privé décide où partent tous ses documents : il se règle
 * par sa titulaire, sur la fiche de l'espace, dans l'appli. Ni l'administrateur qui le crée (il
 * y monterait un Drive dont il est membre), ni l'assistant (un mail piégé ferait de même).
 * Le refus, ou null.
 */
export function refusDriveEspacePrive(r: {
  privee: boolean
  /** Le geste pose ou change l'id du Drive partagé propre. */
  driveChange: boolean
  /** À la création de l'entité (par un administrateur). */
  creation: boolean
  viaAssistant: boolean
}): string | null {
  if (!r.privee || !r.driveChange || !(r.creation || r.viaAssistant)) return null
  return 'Le Drive partagé d’un espace privé se règle par sa titulaire, sur la fiche de l’espace, dans l’appli : ni à sa création, ni via l’assistant.'
}

// Vue des administrateurs (I3) : l'existence de l'espace, ses titulaires, sa dépense IA du mois
// et son plafond, ses boîtes comptées par état ; jamais une adresse ni un contenu.

/** Boîtes connectées d'un espace, comptées par état (les déconnectées n'y sont pas). */
export function resumeBoitesEspace(boites: readonly { etat: EtatBoite }[]) {
  const connectees = boites.filter((b) => b.etat !== 'deconnectee')
  return {
    nombre: connectees.length,
    actives: connectees.filter((b) => b.etat === 'active').length,
    aReconnecter: connectees.filter((b) => b.etat === 'reconnexion').length,
  }
}

/** Plafond IA d'un espace au plus (10 000 $ par mois), en micro-dollars. */
export const PLAFOND_IA_ESPACE_MAX_MICRO_USD = 10_000_000_000

/** Ce qu'un administrateur règle d'un espace privé : son plafond IA (null : celui par défaut). */
export const espacePriveModification = z
  .object({
    plafondIaMicroUsd: z.number().int().min(0).max(PLAFOND_IA_ESPACE_MAX_MICRO_USD).nullable(),
  })
  .strict()
export type EspacePriveModification = z.infer<typeof espacePriveModification>

// Une tâche qui sort de la cloison de la boîte d'où vient son mail (une tâche née dans
// l'espace, passée sur une entité commune) : l'adresse de la boîte se tait, comme à la relève
// d'un mail qui sort de l'espace (D6), et le lien Gmail (qui la porte) s'efface.

const BOITE_TUE = 'une boîte d’un espace privé'

const echapper = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Le texte (description, note) sans les adresses de ces boîtes ni leurs liens Gmail. Une
 * ligne qui ne tenait qu'au lien (« Mail d’origine : … », le lien seul) disparaît.
 */
export function taireBoitesDansTexte(texte: string, adresses: readonly string[]): string {
  if (adresses.length === 0) return texte
  const motifs = adresses.map((a) => echapper(a))
  const liens = new RegExp(
    `https://mail\\.google\\.com/mail/\\?authuser=(?:${adresses
      .flatMap((a) => [echapper(encodeURIComponent(a)), echapper(a)])
      .join('|')})[^\\s)>»]*`,
    'gi',
  )
  const dansLaBoite = new RegExp(`la boîte (?:${motifs.join('|')})`, 'gi')
  const seule = new RegExp(motifs.join('|'), 'gi')
  const lignes: string[] = []
  for (const ligne of texte.split('\n')) {
    const sansLien = ligne.replace(liens, '')
    if (sansLien !== ligne) {
      const reste = sansLien.trim()
      if (reste === '' || /^Mail d’origine\s*:?$/.test(reste)) continue
      lignes.push(sansLien.trimEnd())
      continue
    }
    lignes.push(ligne)
  }
  return lignes.join('\n').replace(dansLaBoite, BOITE_TUE).replace(seule, BOITE_TUE)
}
